import React, { useState } from "react"
import { toast } from "react-toastify"
import { Button } from "~/components/reusable/Button"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { Input } from "~/components/reusable/Input"
import Select from "~/components/reusable/Select"
import { Textarea } from "../reusable/Textarea"
import { type SavedTableView } from "~/server/api/routers/tableView"
import { api } from "~/utils/api"
import { Alert } from "../reusable/Alert"
import { ShortcutTooltip } from "../reusable/ShortcutTooltip"
import { Tooltip } from "../reusable/Tooltip"
import { Eye, UserCircle, Users } from "@phosphor-icons/react"
import { useUser } from "~/providers/userProvider"
import { trackEvent } from "~/hooks/useTrackingService"
import { type OptionType } from "~/models/enum"
import { useView, type ViewSaveSource } from "~/hooks/useView"
import { useFavorites } from "~/hooks/useFavorites"
import { canModifySavedView, type ViewScope } from "~/server/api/utils/viewAccess"

/**
 * Compact ownership indicator for a saved view. Personal views (the default) show
 * nothing — only the exceptions are marked: an icon + tooltip instead of a wide text
 * badge. Replaces the "Shared" / "Other" `<Badge>`.
 */
export function ViewScopeIcon({
  type,
  withTooltip = true,
}: {
  type?: ViewScope
  /**
   * Wrap the icon in its explanatory tooltip. Off inside the ViewPill, where the pill is already
   * a details-hover-card trigger — showing both at once double-tips the same icon (PRO-1339).
   */
  withTooltip?: boolean
}) {
  if (!type || type === "personal") return null

  const isShared = type === "shared"
  const Icon = isShared ? Users : UserCircle
  const title = isShared ? "Shared with everyone" : "Owned by another user"

  const icon = (
    <Icon size={14} weight="bold" className="shrink-0 text-fg-subtle" aria-label={title} />
  )

  return withTooltip ? <Tooltip title={title}>{icon}</Tooltip> : icon
}

interface DeleteViewProps {
  view: SavedTableView
  onDelete: () => void
  customTrigger?: React.ReactNode
  gridTableId?: string
  disabled?: boolean
}

export function DeleteView({
  view,
  onDelete,
  customTrigger,
  gridTableId,
  disabled,
}: DeleteViewProps) {
  const utils = api.useUtils()
  const { removeView } = useFavorites()

  const deleteView = api.savedTableView.delete.useMutation({
    onSuccess: () => {
      if (view.id !== undefined) removeView(view.id)
      onDelete()
      toast.success(`The view "${view.name}" was successfully deleted.`)
      utils.savedTableView.get.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleDelete = async () => {
    await deleteView.mutateAsync(
      { id: view.id! },
      {
        onSuccess: () => {
          trackEvent("View Deleted", {
            "View Name": view.name,
            "Table ID": gridTableId,
          })
        },
      },
    )
  }

  return (
    <ConfirmationDialog
      title="Are you sure?"
      description={`Are you sure you want to delete the "${view.name}" view. This action cannot be undone. ${view.userId ? "" : " THIS IS A SHARED VIEW AND WILL BE DELETED FOR EVERYONE. So be sure that nobody depends on it."}`}
      confirmText="Delete"
      variant="danger"
      onConfirm={handleDelete}
      trigger={
        customTrigger ?? (
          <Button
            variant="danger"
            disabled={disabled}
          >{`Delete ${view.userId ? "" : "shared"} view`}</Button>
        )
      }
    />
  )
}

interface ManageViewProps {
  /** Omit when the dialog is only opened through `open` */
  trigger?: React.ReactNode
  /** Wraps the trigger in a bar-style tooltip (ShortcutTooltip without a shortcut). */
  triggerTooltip?: string
  mode?: "create" | "update"
  tableIdOverride?: string
  /** Read-only label of the base view this view is built on (e.g. the active preset). */
  baseLabel?: string
  /** The view this dialog operates on in update mode. Defaults to the active view. */
  view?: SavedTableView
  /** Sent as `Source` with `View Saved` */
  source: ViewSaveSource
  /** Controlled open state, for opening without the trigger */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ManageView({
  trigger,
  triggerTooltip,
  mode,
  tableIdOverride,
  baseLabel,
  view,
  source,
  open: openProp,
  onOpenChange,
}: ManageViewProps) {
  const {
    handleSaveView,
    handleUpdateView,
    backToDefault,
    refetchViews,
    setViewName,
    viewName,
    viewDescription,
    setViewDescription,
    setIsGlobalView,
    isGlobalView,
    finalViews,
    currentView,
    isCurrentViewChanged,
    gridTableId,
  } = useView({ applyOnMount: false, tableIdOverride })

  const targetView = view ?? currentView

  // The active view also persists the live grid layout on update, so a layout change counts
  // as a change even when the title/visibility are untouched.
  const targetIsActiveView = targetView?.id !== undefined && targetView.id === currentView?.id
  const nameChanged = !!targetView && viewName !== targetView.name
  const descriptionChanged = !!targetView && viewDescription !== (targetView.description ?? "")
  const visibilityChanged = !!targetView && isGlobalView !== (targetView.userId === null)
  const layoutChanged = targetIsActiveView && isCurrentViewChanged
  const nameTaken = finalViews.some((v) => v.name === viewName && v.id !== targetView?.id)
  const hasChanges = nameChanged || descriptionChanged || visibilityChanged || layoutChanged

  // First blocker that applies disables "Update view"; its reason feeds the tooltip.
  const updateDisabledReason =
    [
      { when: viewName.length === 0, reason: "Enter a title for the view" },
      { when: nameTaken, reason: "A view with this name already exists" },
      {
        when: !hasChanges,
        reason: "Make a change to the title, description or visibility to update the view",
      },
    ].find((blocker) => blocker.when)?.reason ?? ""
  const updateDisabled = updateDisabledReason !== ""

  // "Create view" only needs a non-empty, unused name.
  const createDisabled = viewName.length === 0 || finalViews.some((v) => v.name === viewName)

  const [internalOpen, setInternalOpen] = useState(false)
  const open = openProp ?? internalOpen
  const [isSaving, setIsSaving] = useState(false)
  const setOpen = (isOpen: boolean) => {
    // A host that unmounts this dialog on close would also drop the save's `onSuccess`
    if (!isOpen && isSaving) return
    if (openProp === undefined) setInternalOpen(isOpen)
    onOpenChange?.(isOpen)
  }

  /** Closes once the save lands; a failed save keeps the dialog open (the mutation toasts it) */
  const saveThenClose = async (save: () => Promise<void>) => {
    setIsSaving(true)
    try {
      await save()
    } catch {
      return
    } finally {
      setIsSaving(false)
    }
    if (openProp === undefined) setInternalOpen(false)
    onOpenChange?.(false)
  }

  // Reset the form values when the dialog opens to match the target view, however it was opened
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open && mode === "update" && targetView) {
      setViewName(targetView.name)
      setViewDescription(targetView.description ?? "")
      setIsGlobalView(targetView.userId === null)
    } else if (open && mode === "create") {
      setViewName("")
      setViewDescription("")
      setIsGlobalView(false)
    }
  }

  const visibilityOptions: OptionType[] = [
    { label: "Only me", value: "false" },
    { label: "Everyone", value: "true" },
  ]

  const dialogTrigger = trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      {triggerTooltip && dialogTrigger ? (
        <ShortcutTooltip label={triggerTooltip}>{dialogTrigger}</ShortcutTooltip>
      ) : (
        dialogTrigger
      )}
      <DialogContentV2 className="max-w-[480px]">
        <DialogHeaderV2>
          <DialogTitle>{mode === "create" ? "Create view" : "Update view"}</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2>
          <Alert variant="info">
            Save the currently displayed columns, aggregation and filters as a view. The saved view
            will only be available on this page.
          </Alert>
          {baseLabel && (
            <div className="flex flex-row items-center justify-between gap-2 rounded-lg bg-subtle px-3 py-2">
              <span className="label-base text-fg-subtle">Based on</span>
              <span className="label-base font-medium">{baseLabel}</span>
            </div>
          )}
          <Input
            containerClassName="w-full"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            label="Title"
            placeholder="Enter a title for the view"
          />

          <Textarea
            containerClassName="w-full"
            value={viewDescription}
            onChange={(e) => setViewDescription(e.target.value)}
            label="Description"
            placeholder="Optional description"
          />

          <Select
            requires="edit"
            className="w-full"
            value={visibilityOptions.find((option) => option.value === isGlobalView.toString())}
            onChange={(value) => setIsGlobalView(value?.value === "true")}
            options={visibilityOptions}
            label="Visibility"
            leftIcon={isGlobalView ? <Users /> : <Eye />}
          />
        </DialogBodyV2>
        <DialogFooterV2 className="flex flex-row justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {targetView && mode === "update" && (
              <DeleteView
                view={targetView}
                gridTableId={gridTableId}
                disabled={isSaving}
                onDelete={() => {
                  refetchViews()
                  if (targetView.id === currentView?.id) backToDefault()
                  setOpen(false)
                }}
              />
            )}
          </div>
          <div className="flex flex-row gap-3">
            {targetView && mode === "update" && (
              <Tooltip title={updateDisabledReason}>
                <Button
                  disabled={updateDisabled}
                  loading={isSaving}
                  onClick={() => saveThenClose(() => handleUpdateView(targetView, source))}
                >{`Update ${targetView.userId ? "" : "shared"} view`}</Button>
              </Tooltip>
            )}
            {mode === "create" && (
              <Tooltip title={createDisabled ? "Change the name to create a new view" : ""}>
                <Button
                  disabled={createDisabled}
                  loading={isSaving}
                  onClick={() => saveThenClose(() => handleSaveView(undefined, source))}
                >
                  {`Create ${isGlobalView ? "shared" : "new"} view`}
                </Button>
              </Tooltip>
            )}
          </div>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}

export function ManageViewOptions({
  isCurrentViewChanged = false,
  supportsViews,
  hasCurrentView = false,
  tableIdOverride,
  baseLabel,
}: {
  isCurrentViewChanged: boolean
  supportsViews: boolean
  hasCurrentView?: boolean
  tableIdOverride?: string
  baseLabel?: string
}) {
  const { permissions, userId } = useUser()
  const { currentView } = useView({ applyOnMount: false, tableIdOverride })

  // Don't render if views aren't supported on this page
  if (!supportsViews) {
    return null
  }

  // When on default view (no currentView), only show "Create view"
  // When on a saved view and changes were made, show both "Save as new" and "Update view"
  const showUpdateButton = isCurrentViewChanged && hasCurrentView

  return (
    <div className="flex items-center gap-2">
      {showUpdateButton ? (
        <>
          <ManageView
            source="Filter Row"
            trigger={
              <Button size="sm" variant="neutral">
                Save as new view
              </Button>
            }
            mode="create"
            tableIdOverride={tableIdOverride}
            baseLabel={baseLabel}
          />

          {canModifySavedView(permissions, currentView, userId) ? (
            <ManageView
              source="Filter Row"
              trigger={
                <Button size="sm" variant="neutral">
                  Update view
                </Button>
              }
              mode="update"
              tableIdOverride={tableIdOverride}
              baseLabel={baseLabel}
            />
          ) : (
            <Tooltip title="You can only edit your own views">
              <Button size="sm" variant="neutral" disabled>
                Update view
              </Button>
            </Tooltip>
          )}
        </>
      ) : hasCurrentView ? null : (
        <ManageView
          source="Filter Row"
          trigger={
            <Button size="sm" variant="neutral">
              Create view
            </Button>
          }
          mode="create"
          tableIdOverride={tableIdOverride}
          baseLabel={baseLabel}
        />
      )}
    </div>
  )
}
